import React, { useEffect, useState, MouseEvent } from 'react';
import { Box, Typography, Divider, Grid, ClickAwayListener, Popover } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../store';
import useStyles from './styles';
import {
  Field,
  SelectedDeviceAccessor,
  selectedDeviceAccessors,
  selectedDeviceAccessorsORB
} from '../../pages/device-detail/device-detail-accessors';
import { getResult } from '../../store/search/actions';
import { getRestrictedStatus } from '../../store/devices/actions';
import { formatDeviceDetails, getCDMAPreferred, availableDeviceTypes } from './calls';
import EcmDetails from './ecm-info';

const unpack = (splitArray: string[], node: any): string => {
  const { length } = splitArray;
  let iterator = 0;
  let valueAccumulator = node;
  while (iterator < length) {
    valueAccumulator =
      valueAccumulator[splitArray[iterator]] && valueAccumulator[splitArray[iterator]] !== null
        ? valueAccumulator[splitArray[iterator]]
        : '';
    iterator += 1;
  }
  return valueAccumulator;
};

const DeviceDetails = ({
  onChange = () => { },
  details,
  notEditable,
}: {
  onChange?: Function;
  details?: any;
  notEditable?: boolean;
}) => {
  const selectedEntity: any = useSelector((state: AppState) => state.search.selectedEntity);
  const isRestricted: any = useSelector((state: AppState) => state.devices.restrictedDevice);
  const ecmData: any = useSelector((state: AppState) => state.search.ecmData);
  const serialNumber = useSelector(
    (state: AppState) => state.search.selectedEntity?.assetSerialNumber,
  );

  const [deviceDetailsKey, setDeviceDetailsKey] = useState(0);
  const [displayDetails, setDisplayDetails]: any = useState(null);
  const [suspendedDevice, setSuspendedDevice]: any = useState(false);
  const [iconButtonReference, setIconButtonReference] = useState<HTMLElement | null>(null);
  const [orbDisable, setOrbDisable]: any = useState(false);
  const [checkboxStates, setCheckboxStates] = useState<{ [key: string]: boolean }>({});
  const open = Boolean(iconButtonReference);
  const offset = iconButtonReference ? iconButtonReference.offsetLeft : 0;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setIconButtonReference(event.currentTarget.parentElement);
  };
  const clickAway = () => {
    setIconButtonReference(null);
  };
  // const [open,setOpen]=useState(false);
  const dispatch = useDispatch();
  const classes = useStyles();

  const handleInput = (sectionId: string, event: any) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (
      event.target.name === 'deviceStatus' &&
      displayDetails &&
      Object.prototype.hasOwnProperty.call(displayDetails, event.target.name)
    ) {
      displayDetails[event.target.name] = value;
      setDisplayDetails(displayDetails);
      if (value) {
        onChange(sectionId, event.target.name, 'disabled');
        setSuspendedDevice(true);
      }
      else {
        onChange(sectionId, event.target.name, 'active');
        setSuspendedDevice(false);
      }
    }
    else {
      onChange(sectionId, event.target.name, value);
    }
  };

  const handleIsSwapsEnabledInput = (sectionId: string, event: any) => {
    // eslint-disable-next-line no-unneeded-ternary
    const value = event.target.checked === true ? false : true;
    onChange(sectionId, event.target.name, value);
  };

  const handleCDMAPreferredInput = (sectionId: string, event: any) => {
    // eslint-disable-next-line no-unneeded-ternary
    const value = event.target.checked === true ? true : false;
    onChange(sectionId, event.target.name, value);
  };

  const handleORBInput = (sectionId: string, event: any) => {
    // eslint-disable-next-line no-unneeded-ternary
    const value = event.target.checked === true ? true : false;
    onChange(sectionId, event.target.name, value);
  };
  const handleCheckboxChange = (sectionId: string, event: any) => {
  setCheckboxStates((previous) => ({
    ...previous,
    [event.target.name]: event.target.checked, 
  }));

  const handlers: Record<string, (id: string, event: any) => void> = {
    isSwapsEnabled: handleIsSwapsEnabledInput,
    isCDMAPreferred: handleCDMAPreferredInput,
    isRadioDropEnabled: handleORBInput,
  };

  if (handlers[event.target.name]) {
    handlers[event.target.name](sectionId, event);
  }

  handleInput(sectionId, event);
};
  useEffect(() => {
    if (details) {
      setDeviceDetailsKey(deviceDetailsKey + 1);
      setDisplayDetails(formatDeviceDetails(JSON.parse(JSON.stringify(details))));
      const orbFlag= availableDeviceTypes.has(details?.data?.device?.deviceType);
      setOrbDisable(orbFlag);
      setCheckboxStates({
        isSwapsEnabled: details?.data?.device?.isSwapsEnabled ?? false, 
        isCDMAPreferred: !!getCDMAPreferred(details, availableDeviceTypes), 
        isRadioDropEnabled: details?.data?.device?.isRadioDropEnabled ?? false,
        deviceStatus: details?.data?.device?.deviceStatus === "disabled"
      });

    } else {
      const devicelessDetails = !selectedEntity?.deviceKey &&
        selectedEntity && {
        data: {
          asset: {
            assetMake: selectedEntity.assetMake,
            assetSerialNumber: selectedEntity.assetSerialNumber,
          },
        },
      };
      setDisplayDetails(formatDeviceDetails(devicelessDetails));
    }
  }, [details]);

  useEffect(() => {
    if (serialNumber) {
      dispatch(getResult(serialNumber));
      dispatch(getRestrictedStatus(serialNumber));
    }
  }, [serialNumber]);
  const disabledEditing = notEditable || isRestricted;
  const isSuspended = !!(details?.data?.device?.deviceStatus === 'disabled');
  const selectedDeviceAccessor=orbDisable ? selectedDeviceAccessorsORB : selectedDeviceAccessors;

  return (
    <>
      <Grid className={classes.margin} key={deviceDetailsKey}>
        <Box p={2}>
          {displayDetails &&
            Object.keys(displayDetails).length > 0 &&
            selectedDeviceAccessor.map((section: SelectedDeviceAccessor) => (
              <Box key={section.title} id={section.title}>
                {((displayDetails.radio[section.id] &&
                  Object.keys(displayDetails.radio[section.id]).length > 0) ||
                  section.id === 'device') && (
                    <Box p={2}>
                      <Typography variant="h5" className={classes.sectionTitleTypography}>
                        {section.title}
                      </Typography>
                      <Divider />
                      <Grid container>
                        {section.fields.map((field: Field) => {
                          const value =
                            field.accessor !== null
                              ? unpack(field.accessor.split('.'), displayDetails)
                              : '';
                          if (field.accessor) {
                            return (
                              <Grid
                                item
                                xs={12}
                                lg={6}
                                key={`${section.title}-${field.title}`}
                                id={`${section.title}-${field.title}`}
                              >
                                <Box p={1} mr={3} pb={1} display="flex" className={classes.attribute}> 
                              <Typography variant="h6" className={classes.titleTypography}>
                                    {`${field.title}: `}
                                  </Typography>
                                  <div className={classes.valueField}>
                                    {!field.type && field.title === 'Asset Serial Number' && notEditable===undefined? <Typography onClick={handleClick} className={classes.assetValue}>{value}</Typography>
                                      :!field.type && <Typography>{value}</Typography>}
                                    {open && (
                                      <Popover 
                                        open={open}
                                        anchorEl={iconButtonReference}
                                        anchorOrigin={{
                                          vertical: 'bottom',
                                          horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                          vertical: 'top',
                                          horizontal: 'left',
                                        }}
                                      >
                                        <ClickAwayListener onClickAway={clickAway}>
                                          <div
                                        
                                           style={{ width: 500, marginBottom: "5px"}}
                                          >
                                            <EcmDetails ecmData={ecmData} />
                                          </div>
                                        </ClickAwayListener>
                                      </Popover>
                                    )}
                                    {/* // {!field.type && <Typography>{value}</Typography>} */}

                                    {/* {field.type === 'checkbox' && <Typography>{JSON.stringify({ value })}</Typography>} */}
                                    {field.type === 'checkbox' && (
                                      <input
                                        name={field.accessor}
                                        type="checkbox"
                                        className={classes.checkboxStyle}
                                        id={field.accessor}
                                        checked={checkboxStates[field.accessor] ?? false}
                                        // defaultChecked={value.toString() === 'false' || isSuspended || getCDMAPreferred(details, availableDeviceTypes)||isORBChecked}
                                       onChange={(event) => handleCheckboxChange(section.id, event)}
                                        disabled={
                                          field.accessor === 'deviceStatus' ?
                                            disabledEditing
                                            : isSuspended || disabledEditing || suspendedDevice
                                        }
                                      />
                                    )}
                                    {field.type === 'text' &&
                                      (disabledEditing || displayDetails.deviceStatus ? (
                                        <Typography>{value}</Typography>
                                      ) : (
                                        <input
                                          onInput={(event) => handleInput(section.id, event)}
                                          type="text"
                                          name={field.accessor}
                                          defaultValue={value}
                                          disabled={isSuspended}
                                          className={classes.input}
                                        />
                                      ))}
                                    {field.accessor === 'deviceStatus' &&
                                      !isSuspended &&
                                      displayDetails.deviceStatus && (
                                        <p>
                                          Are you sure that you want to suspend the device? Once disabled, If you wish to re-enable the same device in the future, you could click the same checkbox.
                                        </p>
                                      )}
                                  </div>
                                </Box>
                              </Grid>
                            );
                          }
                          return <></>;
                        })}
                      </Grid>
                      <Box display="flex" flexWrap="wrap" p={2} />
                    </Box>
                  )}
              </Box>
            ))}
        </Box>
      </Grid>
    </>
  );
};

export default DeviceDetails;
